import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Building2, Users, HardHat, Briefcase, DollarSign,
  TrendingUp, Ticket, AlertTriangle, Clock, Activity
} from 'lucide-react';

// StatCard component moved outside to prevent re-creation on each render
const StatCard = ({ title, value, icon: Icon, color = 'text-[#ffd108]', subtitle, isDark }) => (
  <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{title}</p>
          <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mt-1`}>{value}</p>
          {subtitle && <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'} mt-1`}>{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} ${color}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SuperAdminDashboard = () => {
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api.get('/api/super-admin/dashboard/stats');
        setStats(res.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [api]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Dashboard</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Welcome back, {admin?.full_name}</p>
        </div>
        <Badge variant="outline" className="text-[#ffd108] border-[#ffd108]">
          {admin?.role?.toUpperCase()}
        </Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Tenants"
          value={stats?.total_tenants || 0}
          icon={Building2}
          subtitle={`${stats?.new_tenants_30d || 0} new this month`}
          isDark={isDark}
        />
        <StatCard
          title="Total Users"
          value={stats?.total_users || 0}
          icon={Users}
          color="text-blue-400"
          isDark={isDark}
        />
        <StatCard
          title="Total Operators"
          value={stats?.total_operators || 0}
          icon={HardHat}
          color="text-green-400"
          isDark={isDark}
        />
        <StatCard
          title="Total Jobs"
          value={stats?.total_jobs || 0}
          icon={Briefcase}
          color="text-purple-400"
          isDark={isDark}
        />
      </div>

      {/* Revenue & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'} lg:col-span-2`}>
          <CardHeader>
            <CardTitle className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <DollarSign className="h-5 w-5 text-[#ffd108]" />
              Monthly Revenue Estimate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-[#ffd108] mb-4">
              ${stats?.monthly_revenue_estimate?.toFixed(2) || '0.00'}
            </div>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(stats?.revenue_by_tier || {}).map(([tier, amount]) => (
                <div key={tier} className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-3`}>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'} capitalize`}>{tier}</p>
                  <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>${amount.toFixed(2)}</p>
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    {stats?.tenants_by_tier?.[tier] || 0} tenants
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <Activity className="h-5 w-5 text-green-400" />
              Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Active Tenants (30d)</span>
              <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-semibold`}>{stats?.active_tenants_30d || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>New Tenants (30d)</span>
              <span className="text-green-400 font-semibold">+{stats?.new_tenants_30d || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>Engagement Rate</span>
              <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-semibold`}>
                {stats?.total_tenants > 0 
                  ? ((stats?.active_tenants_30d / stats?.total_tenants) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tickets Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <Ticket className="h-5 w-5 text-orange-400" />
              Support Tickets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
              {stats?.open_tickets || 0} Open
            </div>
            <div className="space-y-2">
              {Object.entries(stats?.tickets_by_priority || {}).map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {priority === 'urgent' && <AlertTriangle className="h-4 w-4 text-red-400" />}
                    {priority === 'high' && <Clock className="h-4 w-4 text-orange-400" />}
                    {priority === 'medium' && <Clock className="h-4 w-4 text-yellow-400" />}
                    {priority === 'low' && <Clock className="h-4 w-4 text-slate-400" />}
                    <span className={`${isDark ? 'text-slate-300' : 'text-gray-600'} capitalize`}>{priority}</span>
                  </div>
                  <Badge variant={priority === 'urgent' ? 'destructive' : 'secondary'}>
                    {count}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <TrendingUp className="h-5 w-5 text-blue-400" />
              Tenants by Tier
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(stats?.tenants_by_tier || {}).map(([tier, count]) => {
                const percentage = stats?.total_tenants > 0 
                  ? (count / stats.total_tenants) * 100 
                  : 0;
                const colors = {
                  basic: 'bg-slate-500',
                  advanced: 'bg-blue-500',
                  professional: 'bg-[#ffd108]'
                };
                return (
                  <div key={tier}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className={`${isDark ? 'text-slate-300' : 'text-gray-600'} capitalize`}>{tier}</span>
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>{count} ({percentage.toFixed(1)}%)</span>
                    </div>
                    <div className={`h-2 ${isDark ? 'bg-slate-700' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                      <div
                        className={`h-full ${colors[tier] || 'bg-slate-500'} rounded-full transition-all`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
